// app/src/lib/procurement/vendorCompareMath.js — pure comparison math for the
// "compare a new vendor's list" feature. No I/O, no imports — every function
// here is deterministic and unit-tested (test/vendorCompare.test.js), mirroring
// how kanjo-shared/invoiceMath.mjs is the testable core of the OCR path.
//
// The matching itself (which of our items a new supplier's list carries) is done
// by Claude server-side. This module owns the deterministic parts the model
// should NOT be trusted with: pack-size → per-unit normalization, the signed
// price delta, and turning "matched pairs" into the gap/price report the UI shows.

/**
 * First integer pack count in a pack-size string, or null when there isn't one.
 * "6/#10 cans" → 6, "12 × 24oz" → 12, "5 lb" → null. Mirrors priceBook's
 * parsePerUnit so unit prices line up with the rest of the app.
 * @param {string=} packSize
 * @returns {number|null}
 */
export function parsePackCount(packSize) {
  if (!packSize) return null;
  const m = String(packSize).match(/(\d+)\s*[/×x*]\s*/i);
  if (!m) return null;
  const count = Number(m[1]);
  return Number.isFinite(count) && count > 0 ? count : null;
}

/**
 * Per-unit price when the pack count is known, else the raw (per-pack) price.
 * @param {number} priceUsd
 * @param {string=} packSize
 * @returns {number}
 */
export function unitPriceUsd(priceUsd, packSize) {
  const count = parsePackCount(packSize);
  if (!count) return priceUsd;
  return round2(priceUsd / count);
}

/**
 * Compare what the new vendor charges against what we pay now, normalized to
 * unit price on both sides. Returns null when a fair comparison isn't possible
 * (either side missing a price, or our price is non-positive).
 * @param {{ ourLastPriceUsd?:number, ourPackSize?:string, vendorPriceUsd?:number, vendorPackSize?:string }} input
 * @returns {{ ourUnitUsd:number, vendorUnitUsd:number, deltaPct:number, cheaper:boolean }|null}
 */
export function comparePrice({ ourLastPriceUsd, ourPackSize, vendorPriceUsd, vendorPackSize } = {}) {
  if (!Number.isFinite(ourLastPriceUsd) || ourLastPriceUsd <= 0) return null;
  if (!Number.isFinite(vendorPriceUsd) || vendorPriceUsd < 0) return null;
  const ourUnitUsd = unitPriceUsd(ourLastPriceUsd, ourPackSize);
  const vendorUnitUsd = unitPriceUsd(vendorPriceUsd, vendorPackSize);
  const deltaPct = round1(((vendorUnitUsd - ourUnitUsd) / ourUnitUsd) * 100);
  return { ourUnitUsd, vendorUnitUsd, deltaPct, cheaper: vendorUnitUsd < ourUnitUsd };
}

/** Below this confidence (%) a "match" is too shaky to claim the vendor carries
 * the item — we drop it, so the item shows up under "Doesn't carry" instead of as
 * a wrong match. */
export const MIN_CONFIDENCE = 75;

/** Normalize a confidence to a 0–100 number. Accepts the model's numeric score,
 * or the legacy high/medium/low buckets. */
export function confidencePct(c) {
  if (typeof c === 'number' && Number.isFinite(c)) return Math.max(0, Math.min(100, c));
  if (c === 'high') return 90;
  if (c === 'medium') return 75;
  if (c === 'low') return 40;
  return 70; // unknown → just under the bar
}

/**
 * Merge Claude's matched pairs with the items we buy into the report the Buy
 * screen renders: `matches` (items the new vendor carries, with a price
 * comparison when both prices exist) and `missing` (items we buy that the new
 * vendor's list does NOT carry — the gaps that keep us on our current supplier).
 *
 * A matched pair is only counted as "carried" when its confidence is at least
 * `minConfidence` (default 75) — a shaky match (e.g. ginger → garlic) is dropped
 * and the item falls into `missing` rather than being shown as a wrong match.
 * Match rows referencing an unknown itemId are also dropped (defensive against a
 * model hallucinating an id).
 *
 * @param {Array<{ itemId:string, name:string, category?:string, packSize?:string, lastPriceUsd?:number }>} ourItems
 * @param {Array<{ itemId:string, vendorDescription:string, vendorPriceUsd?:number, vendorPackSize?:string, confidence?:string|number }>} matches
 * @param {{ minConfidence?: number }} [opts]
 */
export function buildReport(ourItems, matches, { minConfidence = MIN_CONFIDENCE } = {}) {
  const byId = new Map((ourItems || []).map((it) => [it.itemId, it]));
  const matchedIds = new Set();
  const reportMatches = [];

  for (const m of matches || []) {
    const it = byId.get(m.itemId);
    if (!it) continue; // unknown id — ignore
    const confidence = confidencePct(m.confidence);
    if (confidence < minConfidence) continue; // too shaky → leave it for `missing`
    matchedIds.add(it.itemId);
    reportMatches.push({
      itemId: it.itemId,
      ourName: it.name,
      category: it.category,
      vendorDescription: m.vendorDescription,
      confidence,
      price: comparePrice({
        ourLastPriceUsd: it.lastPriceUsd,
        ourPackSize: it.packSize,
        vendorPriceUsd: m.vendorPriceUsd,
        vendorPackSize: m.vendorPackSize,
      }),
    });
  }

  const missing = (ourItems || [])
    .filter((it) => !matchedIds.has(it.itemId))
    .map((it) => ({ itemId: it.itemId, name: it.name, category: it.category, lastPriceUsd: it.lastPriceUsd }));

  return { matches: reportMatches, missing };
}

/** Human label for a price comparison, e.g. "12% cheaper" / "25% pricier". */
export function priceDeltaText(price) {
  if (!price || !Number.isFinite(price.deltaPct)) return '';
  const abs = Math.abs(price.deltaPct);
  const n = Number.isInteger(abs) ? abs : abs.toFixed(1);
  return price.deltaPct < 0 ? `${n}% cheaper` : price.deltaPct > 0 ? `${n}% pricier` : 'same price';
}

/**
 * Render the report as a plain-text summary suitable for sharing (email / SMS /
 * messaging) or copying. Lists what the new vendor carries (with the price delta
 * where known) and the gaps they don't carry.
 * @param {{ vendorName?:string, report:{matches:Array, missing:Array}, vendorLineCount?:number, ourItemCount?:number }} result
 * @returns {string}
 */
export function formatReportText(result) {
  const { vendorName, report, vendorLineCount, ourItemCount } = result || {};
  const matches = (report && report.matches) || [];
  const missing = (report && report.missing) || [];
  const out = [];
  out.push(`${vendorName || 'New vendor'} — supplier comparison (Kanjo)`);
  if (Number.isFinite(ourItemCount) && Number.isFinite(vendorLineCount)) {
    out.push(`Checked ${ourItemCount} of your items against ${vendorLineCount} products.`);
  }

  out.push('', `THEY CARRY (${matches.length})`);
  if (!matches.length) {
    out.push('- (no overlap with what you buy)');
  } else {
    for (const m of matches) {
      const delta = m.price ? ` (${priceDeltaText(m.price)})` : '';
      out.push(`- ${m.ourName} → ${m.vendorDescription}${delta}`);
    }
  }

  out.push('', `DOESN'T CARRY (${missing.length}) — you'd still need your current supplier`);
  if (!missing.length) {
    out.push('- (they carry everything you buy)');
  } else {
    for (const m of missing) out.push(`- ${m.name}`);
  }

  return out.join('\n');
}

function round1(n) { return Math.round(n * 10) / 10; }
function round2(n) { return Math.round(n * 100) / 100; }
